/**
 * iter131 — Conversation Thread Screen (deep-linked from push notification
 * and Conversations inbox).
 *
 * Fetches the full message thread for one booking and renders it with the
 * shared MessageThread component. Marks the conversation as read on mount.
 * Polls every 10s while visible to reflect new inbound / outbound messages.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Phone, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import MessageThread from '../components/MessageThread';
import OtaLogo from '../components/OtaLogo';
import { whatsappConversationAPI } from '../services/api';
import { getToken } from '../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const ConversationThreadScreen = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [data, setData] = useState({ messages: [], conversation: null, booking: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  // iter132 — compose state
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const inputRef = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setError('');
    try {
      const res = await whatsappConversationAPI.messages(bookingId);
      const r = res?.RESULT || {};
      setData({
        messages: r.messages || [],
        conversation: r.conversation || null,
        booking: r.booking || null,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load messages';
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  // Mark as read once loaded
  useEffect(() => {
    if (!loading && !error && bookingId) {
      whatsappConversationAPI.markRead(bookingId).catch(() => {});
    }
  }, [loading, error, bookingId]);

  // Poll while visible
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load({ silent: true });
    }, 10000);
    return () => clearInterval(iv);
  }, [load]);

  // iter132 — Send outbound owner message
  const handleSend = useCallback(async () => {
    const text = (draft || '').trim();
    if (!text || sending) return;
    setSending(true);
    setSendError('');
    try {
      const token = getToken();
      await axios.post(
        `${API}/whatsapp-conversation/send/${bookingId}`,
        { message_text: text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDraft('');
      await load({ silent: true });
      if (inputRef.current) inputRef.current.focus();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to send';
      setSendError(msg);
    } finally {
      setSending(false);
    }
  }, [draft, sending, bookingId, load]);

  const booking = data.booking || {};
  const convo = data.conversation || {};
  const guest = convo.guest_name || booking.guest_name || 'Guest';
  const ota = convo.ota_source || booking.ota_source || booking.booking_source || '';
  const phone = convo.guest_phone || booking.mobile_number;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="conversation-thread-screen">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-lg mx-auto px-3 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-1 rounded-lg hover:bg-gray-100 text-gray-700"
            data-testid="thread-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-full bg-[#EEF1FC] flex items-center justify-center text-[#1B2A7B] font-semibold flex-shrink-0">
            {guest.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 truncate" data-testid="thread-guest-name">{guest}</div>
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 truncate">
              {ota && <OtaLogo source={ota} className="w-3.5 h-3.5" />}
              <span className="truncate">{ota || 'OTA'}</span>
              <span>•</span>
              <span className="truncate" data-testid="thread-booking-id">{bookingId}</span>
            </div>
            {(booking.check_in_date || booking.check_out_date) && (
              <div className="text-[11px] text-gray-500 truncate" data-testid="thread-stay">
                {formatDate(booking.check_in_date)} → {formatDate(booking.check_out_date)}
              </div>
            )}
          </div>
          {phone && (
            <a
              href={`tel:${phone}`}
              className="p-2 rounded-lg hover:bg-gray-100 text-[#1B2A7B]"
              data-testid="thread-call-btn"
              title="Call guest"
            >
              <Phone className="w-4 h-4" />
            </a>
          )}
          <button
            onClick={() => load()}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            data-testid="thread-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex-1 max-w-lg w-full mx-auto">
        {error && (
          <div className="p-4 m-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="thread-error">
            {error}
          </div>
        )}
        {!error && (
          <MessageThread
            messages={data.messages}
            loading={loading}
            emptyLabel="No WhatsApp messages exchanged with this guest yet."
          />
        )}
      </div>

      {/* Read-only footer — replaced by compose input (iter132) */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 px-3 py-2" data-testid="thread-composer">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
            placeholder="Type a message…"
            disabled={sending}
            className="flex-1 resize-none border border-gray-300 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A7B] focus:border-transparent max-h-32"
            data-testid="thread-composer-input"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-[#152163] text-white flex items-center justify-center hover:bg-[#152163] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            data-testid="thread-composer-send-btn"
            aria-label="Send message"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        {sendError && (
          <div className="text-xs text-red-600 mt-1" data-testid="thread-composer-error">{sendError}</div>
        )}
      </div>
    </div>
  );
};

export default ConversationThreadScreen;
